import { EditUserProps } from "@/components/SettingsSidepanel/settings.types.ts";
import SettingsSheetHeader from "@/components/SettingsSidepanel/SettingsSheetHeader.tsx";
import { useMutation } from "@apollo/client";
import { UPDATE_USER_MUTATION } from "@/components/SettingsSidepanel/settings.graphql.ts";
import {
  UpdateUserMutation,
  UpdateUserMutationVariables,
  UserRole,
  UserRoleEnum,
} from "@/__generated__/graphql";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { Form, Formik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { toast } from "sonner";
import { captureException } from "@sentry/react";
import { Info, Shield, UserRound, ChevronDown } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MONTH_DAY_YEAR_FORMAT, USER_ROLE_ACCESS } from "@/helpers/constants";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button.tsx";
import { formatDate } from "@/helpers/date.ts";
import { Separator } from "@/components/ui/separator.tsx";
import { Switch } from "@/components/ui/switch.tsx";
import { USERS_QUERY } from "@/graphql/queries/common/UsersQuery.ts";
import { UserRoundSearch, User as UserIcon } from "lucide-react";
import { useState } from "react";
import { isDefaultVendor } from "@/helpers/userContext";
import { useUserContext } from "@/contexts/UserContextProvider";

const userIcons = {
  [UserRole.Admin]: (
    <Shield strokeWidth={2} className="size-20 text-yellow-500" />
  ),
  [UserRole.Collaborator]: <UserIcon className="size-20 text-blue-500" />,
  [UserRole.Reviewer]: <UserRoundSearch className="size-20 text-green-500" />,
};
interface FormValues {
  isMaxPerAlertEnabled: boolean;
  isActive: boolean;
  role: UserRoleEnum;
  primaryOfficeId: string | null;
  additionalOfficeIds: string[];
}

const schema = z.object({
  isMaxPerAlertEnabled: z.boolean(),
  isActive: z.boolean(),
  percentage: z.number().optional(),
  role: z.nativeEnum(UserRoleEnum, {
    errorMap: () => ({ message: "Role is required." }),
  }),
});

const EditUser: React.FC<EditUserProps> = ({
  onBack,
  onRequestClose,
  user,
  offices,
}) => {
  const [currentRole, setCurrentRole] = useState<UserRoleEnum | UserRole>(
    user.role,
  );
  const { role, currentUser } = useUserContext();

  const [officeSearch, setOfficeSearch] = useState("");
  const isDefaultVendorProfile = isDefaultVendor(role!, currentUser!);

  const [updateUserMutation] = useMutation<
    UpdateUserMutation,
    UpdateUserMutationVariables
  >(UPDATE_USER_MUTATION, {
    refetchQueries: [
      {
        query: USERS_QUERY,
      },
    ],
  });

  const getAdditionalOfficeOptions = (primaryOfficeId: string) => {
    const companyId = offices.find((o) => o.id === primaryOfficeId)?.company.id;
    if (!companyId) return [];

    return offices.filter(
      (o) => o.company.id === companyId && o.id !== primaryOfficeId,
    );
  };

  return (
    <>
      <SettingsSheetHeader
        title="Edit User"
        onRequestClose={onRequestClose}
        onBack={onBack}
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 p-4 rounded-2xl border border-accent-foreground text-sm">
          <Formik<FormValues>
            validateOnBlur={false}
            initialValues={{
              role: user.role.toString() as UserRoleEnum,
              isActive: user.isActive,
              isMaxPerAlertEnabled: false,
              primaryOfficeId: user.office?.id ?? null,
              additionalOfficeIds: user.additionalOfficeIds ?? [],
            }}
            validationSchema={toFormikValidationSchema(schema)}
            onSubmit={async (
              values,
              { setStatus, setSubmitting, resetForm },
            ) => {
              try {
                const result = await updateUserMutation({
                  variables: {
                    userId: user.id,
                    ...values,
                  },
                });
                if (result && result.data && result.data.updateUser) {
                  const { success, message } = result.data.updateUser;
                  if (!success) {
                    setStatus({ formError: message });
                    toast.error(message);
                  } else {
                    toast.success("User edited successfully!");
                    setCurrentRole(values.role);
                    resetForm({ values });
                  }
                }
              } catch (err) {
                captureException(err);
                setStatus({ formError: err });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              isSubmitting,
              isValid,
              values,
              setFieldValue,
              dirty,
              errors,
            }) => (
              <Form>
                <div className="flex flex-col">
                  <p className="text-xl font-medium text-secondary-foreground">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-base">{user.email}</p>

                  <div className="flex items-center space-x-10">
                    <div className="ml-10">{userIcons[values.role]}</div>
                    <RadioGroup
                      name="role"
                      value={values.role}
                      onValueChange={(val) => setFieldValue("role", val)}
                      className="flex flex-col gap-5 p-5"
                    >
                      {Object.values(UserRoleEnum).map((role: UserRoleEnum) => (
                        <label
                          key={role}
                          htmlFor={role}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <RadioGroupItem value={role} id={role} />
                          <p>{role.toUpperCase()}</p>

                          <div className="flex items-center gap-1 ml-auto">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="size-4" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                {USER_ROLE_ACCESS[role]}
                              </TooltipContent>
                            </Tooltip>

                            {currentRole === role ? (
                              <span className="ml-1">(Current)</span>
                            ) : (
                              <span className="invisible ml-1">(Current)</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
                <div className="text-lg text-secondary-foreground font-medium">
                  Signed up on{" "}
                  {formatDate(user.dateJoined, MONTH_DAY_YEAR_FORMAT)}
                </div>

                {isDefaultVendorProfile && (
                  <>
                    <div className="space-y-1 my-2">
                      <p>Primary Office:</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 pl-2 w-5">
                          <UserRound size={14} />
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                              {values.primaryOfficeId
                                ? offices.find(
                                    (o) => o.id === values.primaryOfficeId,
                                  )?.name
                                : "Select Office"}
                              <ChevronDown />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            className="text-secondary-foreground max-h-44"
                            style={{
                              width: "var(--radix-dropdown-menu-trigger-width)",
                            }}
                            align="end"
                          >
                            <div className="p-2">
                              <input
                                type="text"
                                placeholder="Search office..."
                                className="office-search-input w-full border rounded px-2 py-1 text-sm"
                                value={officeSearch}
                                onChange={(e) =>
                                  setOfficeSearch(e.target.value)
                                }
                                onKeyDownCapture={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                onKeyUpCapture={(e) => e.stopPropagation()}
                                onKeyUp={(e) => e.stopPropagation()}
                              />
                            </div>

                            {[...(offices || [])]
                              .filter((office) =>
                                office.name
                                  .toLowerCase()
                                  .includes(officeSearch.toLowerCase()),
                              )
                              .sort((a, b) =>
                                (a.name || "").localeCompare(
                                  b.name || "",
                                  undefined,
                                  {
                                    sensitivity: "base",
                                  },
                                ),
                              )
                              .map((office) => (
                                <DropdownMenuItem
                                  key={office.id}
                                  className={cn(
                                    "cursor-pointer flex items-center gap-2",
                                    values.primaryOfficeId === office.id &&
                                      "bg-accent-foreground",
                                  )}
                                  onClick={() => {
                                    setFieldValue("primaryOfficeId", office.id);
                                  }}
                                >
                                  {office.name}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {errors.primaryOfficeId && (
                        <div className="text-xs text-destructive">
                          {errors.primaryOfficeId}
                        </div>
                      )}
                    </div>
                    {values.primaryOfficeId &&
                      getAdditionalOfficeOptions(values.primaryOfficeId)
                        .length > 0 && (
                        <div className="space-y-1">
                          <p>Additional Office(s):</p>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 pl-2 w-5">
                              <UserRound size={14} />
                            </div>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                                  {values.additionalOfficeIds.length > 0
                                    ? `${values.additionalOfficeIds.length} Office(s) Selected`
                                    : "Select Offices"}
                                  <ChevronDown />
                                </Button>
                              </DropdownMenuTrigger>

                              <DropdownMenuContent
                                className="text-secondary-foreground max-h-44"
                                style={{
                                  width:
                                    "var(--radix-dropdown-menu-trigger-width)",
                                }}
                                align="end"
                              >
                                {getAdditionalOfficeOptions(
                                  values.primaryOfficeId,
                                )
                                  .sort((a, b) =>
                                    (a.name || "").localeCompare(
                                      b.name || "",
                                      undefined,
                                      {
                                        sensitivity: "base",
                                      },
                                    ),
                                  )
                                  .map((office) => (
                                    <DropdownMenuItem
                                      key={office.id}
                                      className={cn(
                                        "cursor-pointer flex items-center gap-2",
                                        values.primaryOfficeId === office.id &&
                                          "bg-accent-foreground",
                                      )}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (
                                          values.additionalOfficeIds.includes(
                                            office.id,
                                          )
                                        ) {
                                          setFieldValue(
                                            "additionalOfficeIds",
                                            values.additionalOfficeIds.filter(
                                              (id) => id !== office.id,
                                            ),
                                          );
                                        } else {
                                          setFieldValue("additionalOfficeIds", [
                                            ...values.additionalOfficeIds,
                                            office.id,
                                          ]);
                                        }
                                      }}
                                    >
                                      <Checkbox
                                        checked={values.additionalOfficeIds.includes(
                                          office.id,
                                        )}
                                      />
                                      {office.name}
                                    </DropdownMenuItem>
                                  ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      )}
                  </>
                )}

                <Separator className="my-4" />

                <div className="space-y-5">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-semibold text-base">Status:</div>
                        <p>Control user's access to Hotspring</p>
                      </div>{" "}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Active</span>
                        <Switch
                          checked={values.isActive}
                          onCheckedChange={(checked) =>
                            setFieldValue("isActive", checked)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row-reverse">
                    <Button
                      type="submit"
                      variant="paint"
                      disabled={isSubmitting || !isValid || !dirty}
                      className=""
                    >
                      Save
                    </Button>
                  </div>
                </div>

                <Separator className="my-4" />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default EditUser;
